import InputField from '@components/inputField';
import { useAuthStore } from '@container/Auth';
import { SHOP_SELECTED_KEY } from '@helpers/constants';
import { REACT_APP_FOX_KIT_URL, REACT_APP_PURCHASE_URL } from '@helpers/env';
import { showToast } from '@plugins/toast';
import { addPurchaseCodeService } from '@services/license';
import { createNewShopService, updateShopService } from '@services/shop';
import {
  Banner,
  ChoiceList,
  DisplayText,
  FormLayout,
  Heading,
  InlineError,
  Link,
  Modal,
  TextContainer,
  TextStyle
} from '@shopify/polaris';
import { isEqual } from 'lodash';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

const FormPurchaseCode = ({ value, onChange, autoFocus = false, readOnly }) => {
  return (
    <FormLayout>
      <div style={{ marginTop: 30 }}>
        <TextContainer>
          <Heading>Theme purchase code</Heading>
          <TextStyle>
            Add your purchase code to unlock all the powerful features of FoxHome.{' '}
            <Link url={'https://docs.minimog.co/getting-started/purchase-code-and-activation'} external>
              Learn more.
            </Link>
          </TextStyle>
        </TextContainer>
      </div>
      <InputField
        label={'Purchase code'}
        value={value}
        autoFocus={autoFocus}
        onChange={(v) => onChange('purchaseCode', v)}
        helpText={
          <TextStyle>
            If you don't have one, you can{' '}
            <Link url={REACT_APP_PURCHASE_URL} external>
              buy one here.
            </Link>
          </TextStyle>
        }
        readOnly={readOnly}
        disabled={readOnly}
      />
    </FormLayout>
  );
};

const FormCustomApp = ({ value, onChange }) => {
  return (
    <FormLayout>
      <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #ebebeb' }}>
        <TextContainer>
          <DisplayText size={'small'}>
            <strong>API credentials</strong>
          </DisplayText>
          <TextStyle variation={'subdued'}>
            <strong>FoxHome</strong> uses these credentials to work with your store, kindly create a custom app to get credentials.{' '}
            <Link url={'https://docs.minimog.co/foxhome/getting-started/how-to-sign-up-in-foxhome'} external>
              Learn more.
            </Link>
          </TextStyle>
        </TextContainer>
      </div>
      <InputField
        value={value?.accessToken}
        onChange={(v) => onChange('accessToken', v)}
        type={'password'}
        label={'Admin API access token'}
        autoComplete={'off'}
      />
      <InputField
        value={value?.apiSecretKey}
        onChange={(v) => onChange('apiSecretKey', v)}
        type={'password'}
        label={'API secret key'}
        autoComplete={'off'}
      />
    </FormLayout>
  );
};

const FormShopifyApp = forwardRef((props, ref) => {
  const { store, currentUserId } = props;
  return (
    <form ref={ref} action={`${REACT_APP_FOX_KIT_URL}/auth`} method="GET">
      <input type="hidden" name="shop" value={store} />
      <input type="hidden" name="userId" value={currentUserId} />
      <input type="hidden" name="source" value={'foxhome'} />
    </form>
  );
});

const FormApp = forwardRef(({ value, onChange, isEdit, currentUserId, readOnlyPurchaseCode }, ref) => {
  return (
    <>
      {!isEdit && (
        <Modal.Section>
          <ChoiceList
            title="Choose which method you want to use to connect store"
            choices={[
              { label: 'FoxKit app (Recommended)', value: 'foxkit' },
              { label: 'Custom app', value: 'foxhome' },
            ]}
            selected={[value?.appType]}
            onChange={(v) => onChange('appType', v?.[0])}
          />
        </Modal.Section>
      )}
      <Modal.Section subdued>
        {!isEdit && value?.appType === 'foxkit' && (
          <div style={{ marginBottom: '1.6rem' }}>
            <p style={{marginBottom: '1.6rem'}}>
              This method requires the{' '}
              <Link url={'https://foxecom.link/foxkit'} external>
                FoxKit app
              </Link>
              , which should be installed on your store.
            </p>
            <Banner status={'warning'}>
              <p>By using this method, you agree that you authorize FoxHome to interact with your store through FoxKit permissions.</p>
            </Banner>
          </div>
        )}
        <InputField
          value={value?.store}
          onChange={(v) => onChange('store', v)}
          placeholder="your-store-domain.myshopify.com"
          label={'Store domain'}
          disabled={isEdit}
          autoFocus={isEdit}
          helpText={isEdit ? 'You cannot edit the store domain, please add a new one instead.' : 'Enter your store domain'}
        />
        {value?.appType === 'foxhome' && <FormCustomApp value={value} onChange={onChange} />}
        {value?.appType === 'foxkit' && !isEdit && <FormShopifyApp ref={ref} store={value?.store} currentUserId={currentUserId} />}
        {value?.appType === 'foxhome' || isEdit ? (
          <FormPurchaseCode readOnly={readOnlyPurchaseCode} value={value?.purchaseCode} onChange={onChange} />
        ) : null}
      </Modal.Section>
    </>
  );
});

export default function AddStoreModal({ onClose, payload, onConfirm, purchaseCodeOnly }) {
  const [value, setValue] = useState({
    accessToken: '',
    purchaseCode: '',
    apiSecretKey: '',
    store: '',
    appType: 'foxkit',
  });
  const [loading, setLoading] = useState(false);
  const { handleFetchShop, currentUser } = useAuthStore();
  const [error, setError] = useState('');

  useEffect(() => {
    setValue((prev) => ({
      ...prev,
      accessToken: payload?.accessToken,
      purchaseCode: payload?.license?.code,
      apiSecretKey: payload?.apiSecretKey,
      store: payload?.shop,
      appType: payload?.appType ?? 'foxkit',
    }));
  }, [payload]);

  const handleChangeValue = (field, newValue) => {
    setError('');
    setValue((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const handleConfirm = async () => {
    console.log('confirm');

    try {
      setLoading(true);

      const shopifyDomain = value?.store
        .replace(/\s/g, '')
        .replace(/(https?:).*?/i, '')
        .replace(/\//g, '')
        .replace(/\.com.*/, '.com');

      console.log(purchaseCodeOnly);

      if (purchaseCodeOnly) {
        await addPurchaseCodeService(payload?.shop, value?.purchaseCode);
      } else {
        const regexShopifyDomain = /^[a-zA-Z0-9][a-zA-Z0-9\-]*\.myshopify\.com/;

        if (!regexShopifyDomain.test(shopifyDomain)) throw new Error('Invalid shop domain.');

        const requestPayload = {
          shopDomain: shopifyDomain,
          accessToken: value?.accessToken,
          purchaseCode: value?.purchaseCode,
          apiSecretKey: value?.apiSecretKey,
        };
        if (payload?.edit) {
          await updateShopService(payload?._id, requestPayload);
        } else {
          await createNewShopService(requestPayload);
        }
      }

      setLoading(false);

      if (onConfirm) onConfirm(`${shopifyDomain}`);

      showToast({
        message: purchaseCodeOnly ? 'Add purchase code successfully' : `Connect store ${shopifyDomain} successfully`,
      });

      if (payload?.shop === localStorage.getItem(SHOP_SELECTED_KEY)) {
        handleFetchShop();
      }

      onClose();
    } catch (error) {
      console.log(error);
      const newFormErrors = error?.data || error?.message;
      setError(newFormErrors);
      setLoading(false);
    }
  };

  const refForm = useRef();

  const handleSubmit = () => {
    console.log('submit');

    setLoading(true);

    try {
      const regexShopifyDomain = /^[a-zA-Z0-9][a-zA-Z0-9\-]*\.myshopify\.com$/;

      if (!regexShopifyDomain.test(value?.store)) throw new Error('Invalid shop domain.');

      refForm?.current?.submit();
    } catch (error) {
      const newFormErrors = error?.data || error?.message;
      setError(newFormErrors);
      setLoading(false);
    }
  };

  const disabled = useMemo(() => {
    if (purchaseCodeOnly) return !value?.purchaseCode;
    if (value?.appType === 'foxkit') return !value?.store;
    if (payload?.edit) {
      const currentValue = {
        shopDomain: value?.store,
        accessToken: value?.accessToken,
        purchaseCode: value?.purchaseCode,
        apiSecretKey: value?.apiSecretKey,
      };

      const payloadValue = {
        shopDomain: payload?.shop,
        accessToken: payload?.accessToken,
        purchaseCode: payload?.purchaseCode,
        apiSecretKey: payload?.apiSecretKey,
      };
      return isEqual(currentValue, payloadValue);
    }
    return [value?.store, value?.accessToken, value?.apiSecretKey].findIndex((item) => !item) !== -1;
  }, [value, purchaseCodeOnly, payload]);

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={purchaseCodeOnly ? 'Add purchase code to your store' : 'Connect your store to FoxHome'}
      primaryAction={{
        content: purchaseCodeOnly ? 'Add code' : payload?.edit ? 'Update' : 'Connect store',
        onAction: !payload.edit && !purchaseCodeOnly && value?.appType === 'foxkit' ? handleSubmit : handleConfirm,
        disabled: disabled,
        loading,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
          plain: true,
        },
      ]}
    >
      {purchaseCodeOnly ? (
        <Modal.Section>
          <FormPurchaseCode readOnly={payload?.license?.code} value={value?.purchaseCode} onChange={handleChangeValue} autoFocus />
        </Modal.Section>
      ) : (
        <FormApp
          readOnlyPurchaseCode={payload?.license?.code}
          value={value}
          onChange={handleChangeValue}
          isEdit={payload?.edit}
          currentUserId={currentUser._id}
          ref={refForm}
        />
      )}
      {error && (
        <>
          {Array.isArray(error) ? (
            error?.map((err) => {
              const details = err?.details;
              let message = `${err?.message}`;
              return details ? (
                <Modal.Section>
                  <div style={{ marginTop: '1.6rem' }}>
                    <InlineError message={message} />
                    {details && (
                      <div style={{ marginTop: 5, fontSize: 13 }}>
                        <code>{details.join(', ')}</code>
                      </div>
                    )}
                  </div>
                </Modal.Section>
              ) : (
                ''
              );
            })
          ) : (
            <Modal.Section>
              <div style={{ marginTop: '1.6rem' }}>
                <InlineError message={error} />
              </div>
            </Modal.Section>
          )}
        </>
      )}
    </Modal>
  );
}
